import { AnalysisConfig, EnhancedPattern, AnalysisResult, CrossValidationResult } from '@/types/trading-analysis-types';
import { StatisticalValidator } from '../algorithms/StatisticalValidator';
import { IntermediateAnalyzer } from './IntermediateAnalyzer';

export class AdvancedAnalyzer extends IntermediateAnalyzer {
  constructor(config: AnalysisConfig) {
    super(config);
  }

  /**
   * Advanced analysis with statistical validation and cross-validation
   */
  analyzePatterns(sequence: number[]): AnalysisResult {
    const startTime = Date.now();
    
    // Start with intermediate analysis
    const intermediateResult = super.analyzePatterns(sequence);
    let enhancedPatterns = intermediateResult.patterns;

    // Apply statistical validation
    if (this.config.enableStatisticalValidation) {
      enhancedPatterns = this.applyStatisticalValidation(enhancedPatterns, sequence);
    }

    // Apply cross-validation if enabled
    let crossValidationScore: number | undefined;
    if (this.config.enableCrossValidation) {
      crossValidationScore = this.performCrossValidation(sequence);
    }

    const processingTime = Date.now() - startTime;
    const statisticallySignificant = enhancedPatterns.filter(p => 
      p.statisticalMetrics && ['medium', 'high', 'very_high'].includes(p.statisticalMetrics.significance || 'low')
    ).length;

    return {
      mode: 'advanced',
      patterns: enhancedPatterns,
      totalPatterns: enhancedPatterns.length,
      processingTime,
      fuzzyMatches: intermediateResult.fuzzyMatches,
      statisticallySignificant,
      crossValidationScore,
    };
  }

  /**
   * Apply statistical validation to patterns
   */
  private applyStatisticalValidation(patterns: EnhancedPattern[], sequence: number[]): EnhancedPattern[] {
    // Calculate overall win rate
    const totalWins = sequence.filter(x => x === 1).length;
    const overallWinRate = (totalWins / sequence.length) * 100;

    return patterns.map(pattern => {
      const statisticalMetrics = StatisticalValidator.validatePattern(
        pattern,
        overallWinRate,
        this.config.confidenceLevel || 0.95
      );

      return {
        ...pattern,
        statisticalMetrics,
      };
    }).sort((a, b) => {
      // Sort by statistical significance first, then by confidence
      const aSignificance = a.statisticalMetrics?.significance || 'low';
      const bSignificance = b.statisticalMetrics?.significance || 'low';
      
      const significanceOrder = ['low', 'medium', 'high', 'very_high'];
      const aLevel = significanceOrder.indexOf(aSignificance);
      const bLevel = significanceOrder.indexOf(bSignificance);
      
      if (aLevel !== bLevel) {
        return bLevel - aLevel;
      }
      
      return (b.confidenceScore || 0) - (a.confidenceScore || 0);
    });
  }

  /**
   * Perform k-fold cross-validation
   */
  private performCrossValidation(sequence: number[]): number {
    const kFolds = this.config.kFoldSplits || 5;
    const foldSize = Math.floor(sequence.length / kFolds);
    
    if (foldSize < 10) {
      console.warn('Sequence too short for meaningful cross-validation');
      return 0;
    }

    let totalAccuracy = 0;
    const foldResults: Array<{ fold: number; accuracy: number; precision: number; recall: number }> = [];

    for (let i = 0; i < kFolds; i++) {
      const testStart = i * foldSize;
      const testEnd = Math.min((i + 1) * foldSize, sequence.length);
      
      // Create training set (all data except test fold)
      const trainSequence = [
        ...sequence.slice(0, testStart),
        ...sequence.slice(testEnd)
      ];
      
      const testSequence = sequence.slice(testStart, testEnd);
      
      // Train on training set
      const trainPatterns = this.extractPatternsFromSequence(trainSequence);
      
      // Test on test set
      const accuracy = this.evaluatePatterns(trainPatterns, testSequence);
      
      totalAccuracy += accuracy;
      foldResults.push({
        fold: i + 1,
        accuracy,
        precision: accuracy, // Simplified for now
        recall: accuracy     // Simplified for now
      });
    }

    return totalAccuracy / kFolds;
  }

  /**
   * Extract patterns from a sequence for training
   */
  private extractPatternsFromSequence(sequence: number[]): EnhancedPattern[] {
    const tempConfig = { ...this.config, enableCrossValidation: false };
    const tempAnalyzer = new AdvancedAnalyzer(tempConfig);
    const result = tempAnalyzer.analyzePatterns(sequence);
    return result.patterns;
  }

  /**
   * Evaluate patterns against a test sequence
   */
  private evaluatePatterns(patterns: EnhancedPattern[], testSequence: number[]): number {
    if (testSequence.length < 4) return 0;

    let correctPredictions = 0;
    let totalPredictions = 0;

    // Test predictions on sliding window
    for (let i = 3; i < testSequence.length; i++) {
      const inputSequence = testSequence.slice(0, i).join(',');
      const actualResult = testSequence[i];

      try {
        const prediction = this.predictNextTrade(inputSequence, patterns);
        const predictedResult = prediction.winProbability > 50 ? 1 : 0;
        
        if (predictedResult === actualResult) {
          correctPredictions++;
        }
        totalPredictions++;
      } catch (error) {
        // Skip failed predictions
        continue;
      }
    }

    return totalPredictions > 0 ? (correctPredictions / totalPredictions) * 100 : 0;
  }

  /**
   * Advanced prediction with ensemble methods and statistical validation
   */
  predictNextTrade(inputSequence: string, allPatterns: EnhancedPattern[]): {
    winProbability: number;
    lossProbability: number;
    confidenceLevel: 'very_low' | 'low' | 'medium' | 'high' | 'very_high';
    matchingPatterns: number;
    exactMatches: number;
    prediction: 'CERTAIN' | 'UNCERTAIN' | 'REFUSE';
    reason: string;
    fuzzyMatches?: number;
    statisticalConfidence?: number;
    ensembleScore?: number;
  } {
    console.log(`🔬 ADVANCED PREDICTION: Starting with ${allPatterns.length} patterns`);
    
    // Get intermediate prediction (now with fixed weighted calculations)
    const intermediateResult = super.predictNextTrade(inputSequence, allPatterns);
    
    // Filter for statistically significant patterns
    const significantPatterns = this.config.enableStatisticalValidation
      ? StatisticalValidator.filterSignificantPatterns(allPatterns, 50, 'medium')
      : allPatterns;

    console.log(`📊 STATISTICAL FILTERING: ${significantPatterns.length} significant patterns (from ${allPatterns.length} total)`);

    // Calculate statistical confidence
    const statisticalConfidence = StatisticalValidator.calculateOverallConfidence(significantPatterns);
    console.log(`🎯 STATISTICAL CONFIDENCE: ${statisticalConfidence.toFixed(1)}%`);

    // Ensemble scoring (weighted combination of different approaches)
    let ensembleScore: number | undefined;
    let enhancedWinProbability = intermediateResult.winProbability;
    let enhancedLossProbability = intermediateResult.lossProbability;
    
    if (this.config.enableEnsembleMethods && significantPatterns.length > 0) {
      ensembleScore = this.calculateEnsembleScore(inputSequence, allPatterns, significantPatterns);
      
      // If ensemble score differs significantly from intermediate result, blend them
      if (ensembleScore && Math.abs(ensembleScore - intermediateResult.winProbability) > 10) {
        const blendWeight = Math.min(0.4, statisticalConfidence / 100); // Max 40% ensemble weight
        enhancedWinProbability = (intermediateResult.winProbability * (1 - blendWeight)) + (ensembleScore * blendWeight);
        enhancedLossProbability = 100 - enhancedWinProbability;
        console.log(`🎭 ENSEMBLE BLENDING: ${intermediateResult.winProbability.toFixed(1)}% + ${ensembleScore.toFixed(1)}% (${(blendWeight*100).toFixed(1)}% weight) = ${enhancedWinProbability.toFixed(1)}%`);
      }
    }

    // Adjust confidence based on statistical validation and ensemble agreement
    let adjustedConfidenceLevel = intermediateResult.confidenceLevel;
    let confidenceBoost = 0;
    
    if (statisticalConfidence > 70) {
      confidenceBoost += 1;
      console.log(`📈 CONFIDENCE BOOST: +1 level from statistical validation (${statisticalConfidence.toFixed(1)}%)`);
    }
    
    if (ensembleScore && Math.abs(ensembleScore - intermediateResult.winProbability) < 5) {
      confidenceBoost += 1;
      console.log(`🤝 CONFIDENCE BOOST: +1 level from ensemble agreement`);
    }
    
    if (confidenceBoost > 0) {
      const levels: Array<'very_low' | 'low' | 'medium' | 'high' | 'very_high'> = 
        ['very_low', 'low', 'medium', 'high', 'very_high'];
      const currentIndex = levels.indexOf(intermediateResult.confidenceLevel);
      const boostedIndex = Math.min(levels.length - 1, currentIndex + confidenceBoost);
      adjustedConfidenceLevel = levels[boostedIndex];
      console.log(`🚀 FINAL CONFIDENCE: ${intermediateResult.confidenceLevel} → ${adjustedConfidenceLevel}`);
    }

    // Update prediction type based on enhanced confidence
    let enhancedPrediction = intermediateResult.prediction;
    if (adjustedConfidenceLevel === 'very_high' || adjustedConfidenceLevel === 'high') {
      enhancedPrediction = 'CERTAIN';
    } else if (adjustedConfidenceLevel === 'medium') {
      enhancedPrediction = 'UNCERTAIN';
    }

    const finalResult = {
      winProbability: Math.round(enhancedWinProbability * 100) / 100,
      lossProbability: Math.round(enhancedLossProbability * 100) / 100,
      confidenceLevel: adjustedConfidenceLevel,
      matchingPatterns: intermediateResult.matchingPatterns,
      exactMatches: intermediateResult.exactMatches,
      prediction: enhancedPrediction,
      reason: `Advanced analysis: ${intermediateResult.reason} + Statistical confidence: ${statisticalConfidence.toFixed(1)}%${ensembleScore ? ` + Ensemble score: ${ensembleScore.toFixed(1)}%` : ''}`,
      fuzzyMatches: intermediateResult.fuzzyMatches,
      statisticalConfidence,
      ensembleScore,
    };

    console.log(`🏁 ADVANCED RESULT: ${finalResult.winProbability}% win, ${finalResult.confidenceLevel} confidence, ${finalResult.prediction}`);
    return finalResult;
  }

  /**
   * Calculate ensemble score using multiple prediction methods
   */
  private calculateEnsembleScore(
    inputSequence: string,
    allPatterns: EnhancedPattern[],
    significantPatterns: EnhancedPattern[]
  ): number {
    const predictions: number[] = [];

    // Method 1: Basic exact matching
    try {
      const basicResult = super.predictNextTrade(inputSequence, allPatterns);
      predictions.push(basicResult.winProbability);
    } catch (error) {
      // Skip if fails
    }

    // Method 2: Statistically significant patterns only
    if (significantPatterns.length > 0) {
      try {
        const statResult = super.predictNextTrade(inputSequence, significantPatterns);
        predictions.push(statResult.winProbability);
      } catch (error) {
        // Skip if fails
      }
    }

    // Method 3: High confidence patterns only
    const highConfidencePatterns = allPatterns.filter(p => (p.confidenceScore || 0) >= 70);
    if (highConfidencePatterns.length > 0) {
      try {
        const confResult = super.predictNextTrade(inputSequence, highConfidencePatterns);
        predictions.push(confResult.winProbability);
      } catch (error) {
        // Skip if fails
      }
    }

    // Calculate weighted average (more recent methods get higher weight)
    if (predictions.length === 0) return 50;

    const weights = predictions.map((_, i) => 1 + i * 0.2); // Increasing weights
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    
    const weightedSum = predictions.reduce((sum, pred, i) => sum + pred * weights[i], 0);
    return Math.round((weightedSum / totalWeight) * 100) / 100;
  }
}